import Alert from "@material-ui/lab/Alert"
import Typography from "@material-ui/core/Typography"
import Stack from "../Stack"
import Paper from "@material-ui/core/Paper"
import TableContainer from "@material-ui/core/TableContainer"
import Table from "@material-ui/core/Table"
import TableHead from "@material-ui/core/TableHead"
import TableRow from "@material-ui/core/TableRow"
import TableCell from "@material-ui/core/TableCell"
import TableBody from "@material-ui/core/TableBody"
import { Theme, alpha, makeStyles, useMediaQuery } from "@material-ui/core"
import { useTranslation } from "react-i18next"
import clsx from "clsx"
import { AlertTitle } from "@material-ui/lab"
import { kebabCase } from "lodash"
import List from "@material-ui/core/List"
import ListItem from "@material-ui/core/ListItem"
import ListItemText from "@material-ui/core/ListItemText"
import { COLOR_DIVIDER } from "../../constants"

type DiscountCode =
  | "VolumeLessThan3"
  | "VolumeMoreThan50"
  | "VolumeMoreThan100"
  | "VolumeMoreThan150"
  | "VolumeMoreThan200"

type PlanNameKey = "fullAccess" | "basicAccess"
const planTranslationByName: Record<string, PlanNameKey> = {
  "Project Manager": "fullAccess",
  Basic: "basicAccess",
} as const

const BillingSummaryCardMobile = ({ total, summary, discounts }: BillingSummaryCardProps) => {
  const { t } = useTranslation("org")
  const lastDiscountIndex = discounts.length - 1
  const classes = useStyles()
  const discountTranslations = useDiscountTranslations()

  return (
    <Paper variant="outlined">
      {discounts.map((discountCode, index) => {
        const discountTranslation = discountTranslations[discountCode as DiscountCode]
        if (!discountTranslation) return null

        return (
          <Alert
            icon={<span className="iconEmoji">🎁</span>}
            severity="info"
            className={clsx(classes.alert, index === lastDiscountIndex ? "lastItem" : "")}
            data-test="discount-alert"
            key={discountCode}
          >
            <AlertTitle data-test="discount-alert-title">{discountTranslation.title}</AlertTitle>
            {discountTranslation.message}
          </Alert>
        )
      })}
      <Stack spacing={1}>
        <div style={{ padding: 16, borderBottom: `1px solid ${COLOR_DIVIDER}` }}>
          <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
            {t("orgBillingPage.estimatedTotal")}: <span data-test="estimated-total">{total}</span> EUR
          </Typography>
        </div>
        <List dense disablePadding>
          {summary.map((plan) => (
            <ListItem key={plan.name} data-test={`cost-breakdown-${kebabCase(plan.name)}`}>
              <ListItemText
                primary={t(planTranslationByName[plan.name])}
                secondary={
                  <>
                    {t("orgBillingPage.members")}: {plan.quantity}
                    <br />
                    {t("orgBillingPage.pricePerMember")}: {plan.price} EUR
                    <br />
                    <span style={{ fontWeight: 600, marginTop: 8, display: "inline-block" }}>
                      {t("orgBillingPage.total")}: {plan.total} EUR
                    </span>
                  </>
                }
              />
            </ListItem>
          ))}
        </List>
      </Stack>
    </Paper>
  )
}

const BillingSummaryCard = ({ subtotal, total, summary, discounts }: BillingSummaryCardProps) => {
  const classes = useStyles()
  const { t } = useTranslation("org")
  const lastDiscountIndex = discounts.length - 1
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("xs"))
  const discountTranslations = useDiscountTranslations()

  if (isMobile) {
    return <BillingSummaryCardMobile {...{ subtotal, total, summary, discounts }} />
  }

  return (
    <Paper variant="outlined">
      {discounts.map((discountCode, index) => {
        const discountTranslation = discountTranslations[discountCode as DiscountCode]
        if (!discountTranslation) return null

        return (
          <Alert
            icon={<span className="iconEmoji">🎁</span>}
            severity="info"
            className={clsx(classes.alert, index === lastDiscountIndex ? "lastItem" : "")}
            data-test="discount-alert"
            key={discountCode}
          >
            <AlertTitle data-test="discount-alert-title">{discountTranslation.title}</AlertTitle>
            {discountTranslation.message}
          </Alert>
        )
      })}
      <div className={classes.cardRoot}>
        <Stack spacing={1} item style={{ marginTop: -20 }}>
          <Typography variant="subtitle1" color="textSecondary">
            {t("orgBillingPage.estimatedTotal")}
          </Typography>
          <Typography variant="h3">
            {total !== subtotal && (
              <Typography variant="h5" component="span" className={classes.discount}>
                {subtotal}
              </Typography>
            )}
            &nbsp;<span data-test="estimated-total">{total}</span>&nbsp;
            <Typography variant="body1" component="span">
              EUR
            </Typography>
          </Typography>
        </Stack>
        <hr className="divider" />
        <TableContainer component={Paper} className={classes.tableContainer}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("orgBillingPage.accessLevel")}</TableCell>
                <TableCell>{t("orgBillingPage.members")}</TableCell>
                <TableCell>{t("orgBillingPage.pricePerMember")}</TableCell>
                <TableCell>{t("orgBillingPage.total")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary
                .filter((p) => p.quantity)
                .map((plan) => {
                  return (
                    <TableRow key={plan.name} data-test={`cost-breakdown-${kebabCase(plan.name)}`}>
                      <TableCell>{t(planTranslationByName[plan.name])}</TableCell>
                      <TableCell>{plan.quantity}</TableCell>
                      <TableCell>
                        <Typography>{plan.price}</Typography>
                      </TableCell>
                      <TableCell>
                        {plan.total !== plan.subtotal && (
                          <Typography
                            component="span"
                            className={classes.discount}
                            variant="body2"
                            color="textSecondary"
                          >
                            {plan.subtotal}
                          </Typography>
                        )}
                        &nbsp; &nbsp;
                        {plan.total}
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Paper>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  cardRoot: {
    padding: theme.spacing(3),
    display: "flex",
    alignItems: "center",
    "& > div:first-child": {
      width: "38%",
    },
    "& .divider": {
      height: 100,
      width: 1,
      background: theme.palette.divider,
      color: theme.palette.divider,
      border: "none",
      margin: theme.spacing(0, 4, 0, 1),
    },
  },
  alert: {
    borderRadius: 0,
    borderBottom: `1px solid ${alpha(theme.palette.info.main, 0.1)}`,
    background: alpha(theme.palette.info.main, 0.15),
    "& .iconEmoji": {
      padding: 8,
      background: "white",
      borderRadius: "50%",
      height: 48,
      width: 48,
      textAlign: "center",
    },
    "&:first-child": {
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
    },
    "&.lastItem": {
      borderBottom: "none",
    },
  },
  tableContainer: {
    boxShadow: "none",
    border: `1px solid ${theme.palette.divider}`,
    "& th": {
      textTransform: "uppercase",
      padding: theme.spacing(1.5),
      color: theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      letterSpacing: 1,
      fontWeight: theme.typography.fontWeightBold,
    },
    "& td, & th": {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    "& tr:last-child td": {
      borderBottom: "none",
    },
  },
  discount: {
    color: theme.palette.grey[500],
    textDecoration: "line-through",
  },
}))

const useDiscountTranslations = () => {
  const { t } = useTranslation("org")
  return {
    VolumeLessThan3: {
      title: t("orgBillingPage.VolumeLessThan3Title"),
      message: t("orgBillingPage.VolumeLessThan3Message"),
    },
    VolumeMoreThan50: {
      title: t("orgBillingPage.VolumeMoreThan50Title"),
      message: t("orgBillingPage.VolumeMoreThan50Message"),
    },
    VolumeMoreThan100: {
      title: t("orgBillingPage.VolumeMoreThan100Title"),
      message: t("orgBillingPage.VolumeMoreThan100Message"),
    },
    VolumeMoreThan150: {
      title: t("orgBillingPage.VolumeMoreThan150Title"),
      message: t("orgBillingPage.VolumeMoreThan150Message"),
    },
    VolumeMoreThan200: {
      title: t("orgBillingPage.VolumeMoreThan200Title"),
      message: t("orgBillingPage.VolumeMoreThan200Message"),
    },
  }
}

type BillingSummaryCardProps = {
  subtotal: number
  total: number
  summary: { name: string; quantity: number; price: number; total: number; subtotal: number }[]
  discounts: string[]
}

export default BillingSummaryCard
